// alarmAudioMenu.js
// Controle do alerta sonoro, no cabeçalho, ao lado dos demais ícones.
//
// Antes isto era uma barra fixa no rodapé direito, presente em toda tela: ela
// competia por espaço com a própria pilha de avisos que deveria complementar e
// destoava do padrão visual da aplicação. Aqui reaproveita o mesmo idioma do
// menu de perfil — abre ao passar o mouse, fecha ao sair — e some do caminho.
//
// O menu ABRE SOZINHO a cada carregamento enquanto o som estiver bloqueado. O
// navegador começa com o áudio suspenso, e um operador que não perceba isso
// está diante de um supervisório que não vai apitar. Não há popup próprio para
// isso: é o mesmo menu do botão, aberto por conta.
//
// O que NÃO pode sumir é a sinalização de estado degradado: canal caído,
// contexto não resolvido e retrato não semeado continuam renderizados fora do
// menu, porque a premissa da feature é que degradação silenciosa é o pior
// desfecho possível.

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlarmNotifications } from "../scripts/alarmNotificationContext";
import "../styles/alarm-notifications.css";
import {
  AUDIO_ACTIVE,
  AUDIO_BLOCKED,
  AUDIO_MUTED,
  AUDIO_UNSUPPORTED,
} from "../scripts/alarmAudio";

const AlarmAudioMenu = () => {
  const { t } = useTranslation();
  const notifications = useAlarmNotifications();
  const [aberto, setAberto] = useState(false);
  const [avisoJaMostrado, setAvisoJaMostrado] = useState(false);
  const containerRef = useRef(null);

  const audioStatus = notifications?.audioStatus;
  const bloqueado = audioStatus === AUDIO_BLOCKED;

  // Abertura automática, UMA vez por carregamento da página.
  //
  // O estado de "já avisei" vive em memória de propósito, e não em
  // sessionStorage: sessionStorage sobrevive ao F5, então fechar o aviso uma
  // vez o calaria para sempre naquela aba — exatamente o contrário do que se
  // quer. Recarregar tem de voltar a avisar que o alarme está mudo.
  useEffect(() => {
    if (avisoJaMostrado || !bloqueado) return;
    setAberto(true);
    setAvisoJaMostrado(true);
  }, [bloqueado, avisoJaMostrado]);

  // O hover dá conta do mouse; isto cobre toque e o caso do menu que abriu
  // sozinho sem o ponteiro nunca ter entrado nele.
  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (evento) => {
      if (!containerRef.current?.contains(evento.target)) setAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [aberto]);

  // Fora do provider (telas isoladas em teste) o menu não renderiza, em vez de
  // derrubar o cabeçalho inteiro.
  if (!notifications) return null;

  const {
    audioVolume,
    enableAudio,
    toggleMuted,
    changeVolume,
    totalCount,
    channelConnected,
    contextResolved,
    snapshotSeeded,
  } = notifications;

  const indisponivel = audioStatus === AUDIO_UNSUPPORTED;
  const silenciado = audioStatus === AUDIO_MUTED;

  const rotulo = {
    [AUDIO_ACTIVE]: t("Alerta sonoro ativo"),
    [AUDIO_MUTED]: t("Alerta sonoro silenciado"),
    [AUDIO_BLOCKED]: t("Alerta sonoro bloqueado pelo navegador"),
    [AUDIO_UNSUPPORTED]: t("Alerta sonoro indisponível neste navegador"),
  }[audioStatus];

  const icone = {
    [AUDIO_ACTIVE]: "bi bi-volume-up-fill",
    [AUDIO_MUTED]: "bi bi-volume-mute-fill",
    [AUDIO_BLOCKED]: "bi bi-exclamation-triangle-fill",
    [AUDIO_UNSUPPORTED]: "bi bi-volume-off-fill",
  }[audioStatus];

  return (
    <div
      className="alarm-audio-menu-container"
      data-testid="menu-audio"
      ref={containerRef}
      // Mesmo comportamento do menu de perfil: abre ao passar o mouse e fecha
      // ao sair, do ícone ou de qualquer área do próprio menu.
      onMouseEnter={() => setAberto(true)}
      onMouseLeave={() => setAberto(false)}
      // Teclado e toque não têm hover. O foco abre e a saída de foco fecha,
      // o que mantém o controle alcançável sem reintroduzir um clique que
      // esconda o menu. `relatedTarget` dentro do container significa que o
      // foco só andou entre os itens — não é saída.
      onFocus={() => setAberto(true)}
      onBlur={(evento) => {
        if (!containerRef.current?.contains(evento.relatedTarget)) {
          setAberto(false);
        }
      }}
    >
      <button
        type="button"
        className="btn-table alarm-audio-trigger"
        data-testid="indicador-audio"
        data-status={audioStatus}
        aria-expanded={aberto}
        aria-haspopup="true"
        title={rotulo}
        // Sem onClick de propósito: o ícone só ANUNCIA o estado do som. Um
        // clique que alternasse o menu o faria sumir justamente quando o
        // operador foi até ele — e o menu de perfil, que é o padrão da casa,
        // também não responde a clique.
      >
        {/* O disco é um elemento à parte de propósito: o botão reproduz a
            caixa de 48px dos ícones vizinhos (que são SVG com transparência
            em volta), e o disco desenha dentro dela o círculo de 28,4px que
            aqueles SVG desenham. Mesma caixa, mesmo espaçamento. */}
        <span className="alarm-audio-disc">
          <i className={icone} aria-hidden="true" />
          {totalCount > 0 && (
            <span
              className="alarm-audio-count"
              title={`${totalCount} ${t("em estado crítico")}`}
            >
              {totalCount}
            </span>
          )}
        </span>
        <span className="visually-hidden">{rotulo}</span>
      </button>

      {/* Estados degradados: sempre no DOM, nunca escondidos atrás do menu. */}
      {channelConnected === false && (
        <span
          className="alarm-degraded-chip"
          data-testid="canal-caido"
          title={t("Sem conexão com o tempo real")}
        >
          <i className="bi bi-plug-fill" aria-hidden="true" />
          <span className="visually-hidden">
            {t("Sem conexão com o tempo real")}
          </span>
        </span>
      )}

      {!snapshotSeeded && (
        <span
          className="alarm-degraded-chip"
          data-testid="retrato-nao-semeado"
          title={t(
            "Estado inicial dos ativos indisponível: avisos podem faltar",
          )}
        >
          <i className="bi bi-hourglass-split" aria-hidden="true" />
          <span className="visually-hidden">
            {t("Estado inicial dos ativos indisponível: avisos podem faltar")}
          </span>
        </span>
      )}

      {!contextResolved && (
        <span
          className="alarm-degraded-chip"
          data-testid="contexto-nao-resolvido"
          title={t(
            "Contexto do operador indisponível: exibindo todos os ativos",
          )}
        >
          <i className="bi bi-people-fill" aria-hidden="true" />
          <span className="visually-hidden">
            {t("Contexto do operador indisponível: exibindo todos os ativos")}
          </span>
        </span>
      )}

      {aberto && (
        <div className="alarm-audio-menu" role="menu">
          <div className="alarm-audio-menu-status" data-status={audioStatus}>
            <i className={icone} aria-hidden="true" /> {rotulo}
          </div>

          {bloqueado && (
            <button
              type="button"
              className="alarm-audio-menu-item"
              onClick={enableAudio}
            >
              {t("Habilitar som")}
            </button>
          )}

          {!bloqueado && !indisponivel && (
            <>
              <button
                type="button"
                className="alarm-audio-menu-item"
                onClick={toggleMuted}
                aria-pressed={silenciado}
              >
                {silenciado ? t("Reativar som") : t("Silenciar")}
              </button>
              <label className="alarm-audio-volume">
                <span className="visually-hidden">{t("Volume do alerta")}</span>
                <i className="bi bi-volume-down" aria-hidden="true" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={audioVolume}
                  onChange={(event) => changeVolume(event.target.value)}
                  aria-label={t("Volume do alerta")}
                />
              </label>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AlarmAudioMenu;
